import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { formatDate } from "../../utils/dateUtils";
import alertService from "../../utils/alertService";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import DatePicker from "../../components/common/DatePicker";
import TextArea from "../../components/common/TextArea";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import ErrorAlert from "../../components/common/ErrorAlert";
import salesPaymentService from "../../services/salesPaymentService";
import paymentModeService from "../../services/paymentModeService";
import bankService from "../../services/bankService";
import { API_CONFIG } from "../../utils/apiConfig";

function getTodayDateString() {
  const today = new Date();
  const year = String(today.getFullYear());
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function SalesPaymentDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: sellingMasterId } = useEncryptedParams();
  const basePath = "/sales-payment-master";

  // --- States ---
  const [sellingMasterData, setSellingMasterData] = useState(
    location.state?.sellingMaster || null,
  );
  const [payments, setPayments] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [activeBanks, setActiveBanks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingTransaction, setIsSubmittingTransaction] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Form State for Payment Transaction (Add/Edit)
  const [isEditingTransaction, setIsEditingTransaction] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [transactionForm, setTransactionForm] = useState({
    amount: "",
    date: getTodayDateString(),
    paymentModeId: "",
    gstAmount: "",
    tdsAmount: "",
    netAmount: "",
    remarks: "",
    isTds: false,
    isActive: true,
    bankId: "",
    transactionRefNo: "",
    vpa: "",
    chequeNo: "",
    chequeDate: getTodayDateString(),
    attachment: null,
    attachmentPath: "",
    cashSource: ""
  });

  // Load Selling Master and Payments
  const loadAllData = useCallback(async () => {
    if (!sellingMasterId) return;
    setIsLoading(true);
    setError(null);
    try {
      // 1. Fetch payments
      const paymentsData =
        await salesPaymentService.getPaymentsBySellingMasterId(sellingMasterId);
      setPayments(paymentsData);

      // 2. Fetch/resolve Selling Master details
      let masterData = sellingMasterData;
      const response = await salesPaymentService.getSalesPayments({
        limit: 1000,
      });
      const foundMaster = (response.items || []).find(
        (item) => String(item.sellingMasterId) === String(sellingMasterId),
      );

      if (foundMaster) {
        masterData = foundMaster;
      } else if (!masterData) {
        setError("Selling Master record not found.");
        return;
      }

      // Refresh received/remaining values from fresh payments list
      const newReceived = paymentsData.reduce(
        (sum, p) => sum + Number(p.amount),
        0,
      );
      setSellingMasterData({
        ...masterData,
        receivedAmount: newReceived,
        remainingReceivableAmount: Number(
          (masterData.totalAmount - newReceived).toFixed(2),
        ),
        isReceived: Boolean(masterData.isReceived),
      });
    } catch (err) {
      setError(err.message || "Failed to load data.");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellingMasterId]);

  // Load Payment Modes
  useEffect(() => {
    const loadModes = async () => {
      try {
        const modes = await paymentModeService.getActivePaymentModes();
        setPaymentModes(
          modes.map((m) => ({
            label: m.paymentMode,
            value: String(m.id),
            systemKey: m.systemKey
          })),
        );
      } catch (err) {
        console.error("Failed to load payment modes", err);
      }
    };
    loadModes();
  }, []);

  // Load Active Banks
  useEffect(() => {
    const loadBanks = async () => {
      try {
        const banks = await bankService.getActiveBanks();
        setActiveBanks(
          banks.map((b) => ({
            label: `${b.bankName}${b.accountNumber ? ` - ${b.accountNumber}` : ""}`,
            value: String(b.bankId || b.id),
            raw: b,
          }))
        );
      } catch (err) {
        console.error("Failed to load active banks", err);
      }
    };
    loadBanks();
  }, []);

  const selectedBank = useMemo(() => {
    if (!transactionForm.bankId) return null;
    const opt = activeBanks.find((b) => String(b.value) === String(transactionForm.bankId));
    return opt?.raw || null;
  }, [transactionForm.bankId, activeBanks]);

  useEffect(() => {
    if (!sellingMasterId) {
      navigate(basePath);
      return;
    }
    loadAllData();
  }, [sellingMasterId, loadAllData, navigate, basePath]);

  // Reset Form
  const resetTransactionForm = () => {
    setTransactionForm({
      amount: "",
      date: getTodayDateString(),
      paymentModeId: "",
      gstAmount: "",
      tdsAmount: "",
      netAmount: "",
      remarks: "",
      isTds: false,
      isActive: true,
      bankId: "",
      transactionRefNo: "",
      vpa: "",
      chequeNo: "",
      chequeDate: getTodayDateString(),
      attachment: null,
      attachmentPath: "",
      cashSource: ""
    });
    setFormErrors({});
    setIsEditingTransaction(false);
    setEditingTransactionId(null);
    const fileInput = document.getElementById("payment-attachment-input");
    if (fileInput) fileInput.value = "";
  };

  // Recompute netAmount from amount, tdsAmount, gstAmount (all manual except netAmount)
  const recomputeNetAmount = (amount, tdsAmount, gstAmount) => {
    const amt = parseFloat(amount) || 0;
    const tds = parseFloat(tdsAmount) || 0;
    const gst = parseFloat(gstAmount) || 0;
    const netAmt = Number((amt - tds - gst).toFixed(2));
    return amt > 0 ? String(netAmt < 0 ? 0 : netAmt) : "";
  };

  // Handle Form Input Changes
  const handleFormChange = (field, value) => {
    let val = value;
    if (field === "attachment" && val) {
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
      const maxSize = 5 * 1024 * 1024; // 5 MB
      if (!allowedTypes.includes(val.type)) {
        alertService.error("Only PDF, JPG, and PNG files are allowed.");
        val = null;
        const fileInput = document.getElementById("payment-attachment-input");
        if (fileInput) fileInput.value = "";
      } else if (val.size > maxSize) {
        alertService.error("Maximum file size allowed is 5 MB.");
        val = null;
        const fileInput = document.getElementById("payment-attachment-input");
        if (fileInput) fileInput.value = "";
      }
    }
    if (field === "chequeNo") {
      val = val.replace(/[^a-zA-Z0-9]/g, "");
      if (val.length > 10) {
        val = val.slice(0, 10);
      }
    }
    if (field === "vpa") {
      val = val.replace(/[^a-zA-Z0-9@.\-_]/g, "");
      const atCount = (val.match(/@/g) || []).length;
      if (atCount > 1) {
        const firstAtIdx = val.indexOf("@");
        val = val.slice(0, firstAtIdx + 1) + val.slice(firstAtIdx + 1).replace(/@/g, "");
      }
    }

    if (field === "paymentModeId") {
      setTransactionForm((prev) => ({
        ...prev,
        paymentModeId: val,
        bankId: "",
        transactionRefNo: "",
        vpa: "",
        chequeNo: "",
        cashSource: ""
      }));
      setFormErrors((prev) => ({
        ...prev,
        paymentModeId: null,
        bankId: null,
        transactionRefNo: null,
        vpa: null,
        chequeNo: null,
        cashSource: null
      }));
      return;
    }

    if (field === "cashSource") {
      setTransactionForm((prev) => ({
        ...prev,
        cashSource: val,
        bankId: val === "Cash in Hand" ? "" : prev.bankId
      }));
      setFormErrors((prev) => ({
        ...prev,
        cashSource: null,
        bankId: val === "Cash in Hand" ? null : prev.bankId
      }));
      return;
    }

    setTransactionForm((prev) => {
      const updated = { ...prev, [field]: val };
      if (["amount", "tdsAmount", "gstAmount"].includes(field)) {
        return {
          ...updated,
          netAmount: recomputeNetAmount(
            field === "amount" ? val : updated.amount,
            field === "tdsAmount" ? val : updated.tdsAmount,
            field === "gstAmount" ? val : updated.gstAmount,
          ),
        };
      }
      return updated;
    });
    setFormErrors((prev) => ({ ...prev, [field]: null }));
  };

  const GST_RATE = 18; // 18%
  const TDS_RATE = 2; // 2%

  const [isTogglingReceived, setIsTogglingReceived] = useState(false);

  const handleToggleReceivedStatus = async () => {
    if (!sellingMasterId || !sellingMasterData) return;
    const currentStatus = Boolean(sellingMasterData.isReceived);
    const newStatus = !currentStatus;

    const actionTitle = newStatus ? "Complete & Lock Payment" : "Unlock Payment";
    const confirmMessage = newStatus
      ? `Are you sure you want to mark Invoice #${sellingMasterData.invoiceNo || ""} as Fully Received / Completed?<br/><br/><span class="text-xs text-slate-500">This will lock the payment record.</span>`
      : `Are you sure you want to unlock Invoice #${sellingMasterData.invoiceNo || ""}?<br/><br/><span class="text-xs text-slate-500">This will allow adding and editing payment transactions again.</span>`;

    const confirm = await alertService.confirm(confirmMessage, {
      title: actionTitle,
      confirmButtonText: newStatus ? "Yes, Complete Payment" : "Yes, Unlock Payment",
    });

    if (!confirm.isConfirmed) return;

    setIsTogglingReceived(true);
    try {
      await salesPaymentService.toggleReceivedStatus(sellingMasterId, newStatus);
      setSellingMasterData((prev) => ({
        ...prev,
        isReceived: newStatus,
      }));
      alertService.success(
        newStatus
          ? "Payment has been marked as Completed and locked successfully."
          : "Payment has been unlocked successfully."
      );
    } catch (err) {
      alertService.error(err.message || "Failed to update payment status.");
    } finally {
      setIsTogglingReceived(false);
    }
  };

  const handleAmountChange = (value) => {
    let sanitizedVal = value;
    if (sanitizedVal && sanitizedVal.includes("-")) {
      sanitizedVal = sanitizedVal.replace(/-/g, "");
    }
    if (sanitizedVal && sanitizedVal.includes(".")) {
      const parts = sanitizedVal.split(".");
      if (parts[1] && parts[1].length > 2) {
        sanitizedVal = `${parts[0]}.${parts[1].slice(0, 2)}`;
      }
    }

    const amt = parseFloat(sanitizedVal) || 0;

    // Calculate GST (18%) and TDS (2% if enabled)
    const gstAmt = amt > 0 ? Number(((amt * GST_RATE) / 100).toFixed(2)) : 0;
    const tdsAmt = amt > 0 && transactionForm.isTds ? Math.floor((amt * TDS_RATE) / 100) : 0;
    const netAmt = amt > 0 ? Number((amt - gstAmt - tdsAmt).toFixed(2)) : 0;

    setTransactionForm((prev) => ({
      ...prev,
      amount: sanitizedVal,
      gstAmount: amt > 0 ? String(gstAmt) : "",
      tdsAmount: amt > 0 && prev.isTds ? String(tdsAmt) : "0",
      netAmount: amt > 0 ? String(netAmt < 0 ? 0 : netAmt) : "",
    }));

    setFormErrors((prev) => ({ ...prev, amount: null }));
  };

  const handleTdsToggle = (checked) => {
    const amt = parseFloat(transactionForm.amount) || 0;
    const gstAmt = amt > 0 ? Number(((amt * GST_RATE) / 100).toFixed(2)) : 0;
    const tdsAmt = checked && amt > 0 ? Math.floor((amt * TDS_RATE) / 100) : 0;
    const netAmt = amt > 0 ? Number((amt - gstAmt - tdsAmt).toFixed(2)) : 0;

    setTransactionForm((prev) => ({
      ...prev,
      isTds: checked,
      tdsAmount: amt > 0 && checked ? String(tdsAmt) : "0",
      netAmount: amt > 0 ? String(netAmt < 0 ? 0 : netAmt) : "",
    }));
  };

  // Handle Edit Transaction
  const handleEditTransaction = (transaction) => {
    setIsEditingTransaction(true);
    setEditingTransactionId(transaction.id);

    const selectedMode = paymentModes.find(m => String(m.value) === String(transaction.paymentModeId));
    const sysKey = selectedMode ? selectedMode.systemKey : "";
    let cashSourceVal = "";
    if (sysKey === "cash") {
      const hasBank = transaction.bankId || transaction.bank?.bankId || transaction.bank?.id;
      cashSourceVal = hasBank ? "Bank Deposit" : "Cash in Hand";
    }

    const hasTds =
      transaction.isTds !== undefined && transaction.isTds !== null
        ? Boolean(transaction.isTds)
        : Number(transaction.tdsAmount || 0) > 0;

    setTransactionForm({
      amount: String(transaction.amount),
      date: transaction.date
        ? transaction.date.split("T")[0]
        : getTodayDateString(),
      paymentModeId: String(transaction.paymentModeId),
      gstAmount:
        transaction.gstAmount !== null && transaction.gstAmount !== undefined
          ? String(transaction.gstAmount)
          : "",
      tdsAmount:
        transaction.tdsAmount !== null && transaction.tdsAmount !== undefined
          ? String(transaction.tdsAmount)
          : "0",
      netAmount:
        transaction.netAmount !== null && transaction.netAmount !== undefined
          ? String(transaction.netAmount)
          : "",
      remarks: transaction.remarks || transaction.remark || "",
      isTds: hasTds,
      isActive: Boolean(transaction.isActive),
      bankId: transaction.bankId ? String(transaction.bankId) : (transaction.bank?.bankId ? String(transaction.bank.bankId) : (transaction.bank?.id ? String(transaction.bank.id) : "")),
      transactionRefNo: transaction.transactionRefNo || "",
      vpa: transaction.vpa || "",
      chequeNo: transaction.chequeNo || "",
      chequeDate: transaction.chequeDate
        ? transaction.chequeDate.split("T")[0]
        : getTodayDateString(),
      attachment: null,
      attachmentPath: transaction.attachmentPath || "",
      cashSource: cashSourceVal
    });
    setFormErrors({});
  };

  // Handle Delete Transaction
  const handleDeleteTransaction = async (transaction) => {
    const selectedMode = transaction.paymentMode;
    const modeLabel = selectedMode ? selectedMode.paymentMode : `Mode #${transaction.paymentModeId}`;
    const sysKey = selectedMode ? selectedMode.systemKey : "";

    let paymentDetailsArr = [];
    if (transaction.bank) {
      paymentDetailsArr.push(`Bank: ${transaction.bank.bankName}${transaction.bank.accountNumber ? ` (${transaction.bank.accountNumber})` : ""}`);
    }
    if (sysKey === "bank" && transaction.transactionRefNo) {
      paymentDetailsArr.push(`UTR: ${transaction.transactionRefNo}`);
    } else if (sysKey === "upi") {
      if (transaction.vpa) paymentDetailsArr.push(`UPI: ${transaction.vpa}`);
      if (transaction.transactionRefNo) paymentDetailsArr.push(`ID: ${transaction.transactionRefNo}`);
    } else if (sysKey === "cheque") {
      if (transaction.chequeNo) paymentDetailsArr.push(`Cheque: #${transaction.chequeNo}`);
      if (transaction.chequeDate) paymentDetailsArr.push(`Date: ${formatDate(transaction.chequeDate)}`);
    }
    const paymentDetailsStr = paymentDetailsArr.join(" | ") || null;

    const details = {
      date: formatDate(transaction.date),
      paymentMode: modeLabel,
      formattedAmount: `₹${Number(transaction.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      gstAmount: transaction.gstAmount ? `₹${Number(transaction.gstAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : null,
      tdsAmount: transaction.tdsAmount ? `₹${Number(transaction.tdsAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : null,
      netAmount: transaction.netAmount ? `₹${Number(transaction.netAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : null,
      paymentDetails: paymentDetailsStr || null,
      remarks: transaction.remarks || transaction.remark || null
    };

    const confirm = await alertService.confirmPaymentAction(details, "delete");
    if (!confirm.isConfirmed) return;

    try {
      await salesPaymentService.deleteSalesPayment(transaction.id);
      alertService.success("Payment transaction deleted successfully.");

      // Reload payments
      const updatedPayments =
        await salesPaymentService.getPaymentsBySellingMasterId(sellingMasterId);
      setPayments(updatedPayments);

      // Update local stats
      const newReceived = updatedPayments.reduce(
        (sum, p) => sum + Number(p.amount),
        0,
      );
      setSellingMasterData((prev) => ({
        ...prev,
        receivedAmount: newReceived,
        remainingReceivableAmount: Number(
          (prev.totalAmount - newReceived).toFixed(2),
        ),
      }));
    } catch (err) {
      alertService.error(err.message || "Failed to delete transaction.");
    }
  };

  // Handle Submit
  const handleSubmitTransaction = async (e) => {
    e.preventDefault();
    setFormErrors({});

    const errors = {};
    if (!transactionForm.amount || parseFloat(transactionForm.amount) <= 0) {
      errors.amount = "Amount must be greater than 0";
    }
    if (!transactionForm.date) {
      errors.date = "Date is required";
    } else if (sellingMasterData && sellingMasterData.date) {
      const paymentDate = new Date(transactionForm.date);
      const invoiceDate = new Date(sellingMasterData.date);
      paymentDate.setHours(0, 0, 0, 0);
      invoiceDate.setHours(0, 0, 0, 0);
      if (paymentDate < invoiceDate) {
        errors.date = "Payment date cannot be before the invoice date";
      }
    }
    if (!transactionForm.paymentModeId) {
      errors.paymentModeId = "Payment Mode is required";
    }

    const selectedMode = paymentModes.find(m => String(m.value) === String(transactionForm.paymentModeId));
    const sysKey = selectedMode ? selectedMode.systemKey : "";
    const modeLabel = selectedMode ? selectedMode.label : "N/A";

    // Bank is compulsory
    if (sysKey === "cash") {
      if (!transactionForm.cashSource) {
        errors.cashSource = "Cash Source is required";
      } else if (transactionForm.cashSource === "Bank Deposit" && !transactionForm.bankId) {
        errors.bankId = "Bank is required";
      }
    } else {
      if (!transactionForm.bankId) {
        errors.bankId = "Bank is required";
      }
    }

    // --- Cheque-specific format validation ---
    if (sysKey === "cheque") {
      if (transactionForm.chequeNo && /[^a-zA-Z0-9]/.test(transactionForm.chequeNo)) {
        errors.chequeNo = "Cheque number cannot contain special characters";
      } else if (transactionForm.chequeNo && transactionForm.chequeNo.length > 10) {
        errors.chequeNo = "Cheque number cannot exceed 10 characters";
      }
    }

    // --- UPI-specific format validation ---
    if (sysKey === "upi" && transactionForm.vpa?.trim()) {
      const vpaVal = transactionForm.vpa.trim();
      const upiRegex = /^[a-zA-Z0-9.\-_]+@[a-zA-Z0-9.\-_]+$/;
      if (!upiRegex.test(vpaVal)) {
        errors.vpa = "Invalid UPI ID format (e.g., username@bank)";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const payload = {
      sellingMasterId: String(sellingMasterId),
      amount: Number(transactionForm.amount),
      date: new Date(transactionForm.date).toISOString(),
      paymentModeId: String(transactionForm.paymentModeId),
      netAmount:
        transactionForm.netAmount !== ""
          ? Number(transactionForm.netAmount)
          : 0,
      gstAmount:
        transactionForm.gstAmount !== ""
          ? Number(transactionForm.gstAmount)
          : 0,
      tdsAmount:
        transactionForm.isTds && transactionForm.tdsAmount !== ""
          ? Number(transactionForm.tdsAmount)
          : 0,
      isTds: Boolean(transactionForm.isTds),
      remarks: transactionForm.remarks?.trim() || "",
      isActive: true,
      bankId: (sysKey === "cash" && transactionForm.cashSource === "Cash in Hand") ? null : (transactionForm.bankId ? Number(transactionForm.bankId) : null),
      transactionRefNo: (sysKey === "bank" || sysKey === "upi") ? transactionForm.transactionRefNo?.trim() || null : null,
      vpa: (sysKey === "upi") ? transactionForm.vpa?.trim() || null : null,
      chequeNo: (sysKey === "cheque") ? transactionForm.chequeNo?.trim() || null : null,
      chequeDate: (sysKey === "cheque") ? new Date(transactionForm.chequeDate).toISOString() : null,
      attachment: transactionForm.attachment || null,
      attachmentPath: transactionForm.attachmentPath || ""
    };

    let paymentDetailsArr = [];
    const bankOpt = activeBanks.find(b => String(b.value) === String(transactionForm.bankId));
    if (sysKey === "cash") {
      paymentDetailsArr.push(`<div><strong>Cash Source:</strong> ${transactionForm.cashSource}</div>`);
      if (transactionForm.cashSource === "Bank Deposit" && bankOpt) {
        paymentDetailsArr.push(`<div><strong>Bank:</strong> ${bankOpt.label}</div>`);
      }
    } else {
      if (bankOpt) paymentDetailsArr.push(`<div><strong>Bank:</strong> ${bankOpt.label}</div>`);
    }
    if (sysKey === "bank" && transactionForm.transactionRefNo) {
      paymentDetailsArr.push(`<div><strong>UTR:</strong> ${transactionForm.transactionRefNo}</div>`);
    } else if (sysKey === "upi") {
      if (transactionForm.vpa) paymentDetailsArr.push(`<div><strong>UPI ID:</strong> ${transactionForm.vpa}</div>`);
      if (transactionForm.transactionRefNo) paymentDetailsArr.push(`<div><strong>Txn ID:</strong> ${transactionForm.transactionRefNo}</div>`);
    } else if (sysKey === "cheque") {
      if (transactionForm.chequeNo) paymentDetailsArr.push(`<div><strong>Cheque:</strong> #${transactionForm.chequeNo}</div>`);
      if (transactionForm.chequeDate) paymentDetailsArr.push(`<div><strong>Date:</strong> ${formatDate(transactionForm.chequeDate)}</div>`);
    }
    const paymentDetailsStr = paymentDetailsArr.join("") || null;

    const details = {
      date: formatDate(transactionForm.date),
      paymentMode: modeLabel,
      formattedAmount: `₹${Number(transactionForm.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      paymentDetails: paymentDetailsStr || null,
      remarks: transactionForm.remarks?.trim() || null
    };

    const actionType = isEditingTransaction ? "update" : "save";
    const confirm = await alertService.confirmPaymentAction(details, actionType);
    if (!confirm.isConfirmed) return;

    setIsSubmittingTransaction(true);
    try {
      if (isEditingTransaction) {
        await salesPaymentService.updateSalesPayment(
          editingTransactionId,
          payload,
        );
        alertService.success("Payment transaction updated successfully.");
      } else {
        await salesPaymentService.createSalesPayment(payload);
        alertService.success("Payment transaction recorded successfully.");
      }
      resetTransactionForm();

      // Reload payments
      const updatedPayments =
        await salesPaymentService.getPaymentsBySellingMasterId(sellingMasterId);
      setPayments(updatedPayments);

      // Update local Selling Master stats
      const newReceived = updatedPayments.reduce(
        (sum, p) => sum + Number(p.amount),
        0,
      );
      setSellingMasterData((prev) => ({
        ...prev,
        receivedAmount: newReceived,
        remainingReceivableAmount: Number(
          (prev.totalAmount - newReceived).toFixed(2),
        ),
      }));
    } catch (err) {
      alertService.error(err.message || "Failed to save payment transaction.");
    } finally {
      setIsSubmittingTransaction(false);
    }
  };

  const getEffectivePath = () => {
    if (
      location.pathname.includes("/invoice-management/sales-payment-master")
    ) {
      return "/invoice-management/sales-payment-master";
    }
    return basePath;
  };

  if (isLoading && !sellingMasterData) {
    return (
      <div className="p-10 text-center text-slate-500">
        Loading payment details...
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8 px-4 sm:px-0">
      {/* --- Header --- */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate(getEffectivePath())}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-colors"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <h1 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
              Manage Sales <span className="text-primary-600">Payments</span>
            </h1>
          </div>
          {sellingMasterData && (
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-y-1.5 gap-x-3 text-xs font-bold text-slate-500 dark:text-slate-400 md:pl-9 uppercase tracking-widest">
              <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 w-fit">
                Invoice No: {sellingMasterData.invoiceNo || "N/A"}
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="text-primary-600">
                Customer Name: {sellingMasterData.customerName || "N/A"}
              </span>
              {sellingMasterData.companyName && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <span>
                    Company Name: {sellingMasterData.companyName}
                  </span>
                </>
              )}
              <span className="hidden sm:inline">•</span>
              <span>Date: {formatDate(sellingMasterData.date)}</span>
              {sellingMasterData.taxType && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400 border border-amber-200 dark:border-amber-900 text-[10px] w-fit">
                    Tax Type: {sellingMasterData.taxType}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Action / Status Area */}
        {sellingMasterData && (
          <div className="flex items-center gap-3 self-start md:self-center">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${
                sellingMasterData.isReceived
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800"
                  : Number(sellingMasterData.receivedAmount || 0) > 0
                  ? "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-300 dark:border-amber-800"
                  : "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-300 dark:border-rose-800"
              }`}
            >
              <span
                className={`h-2 w-2 rounded-full ${
                  sellingMasterData.isReceived
                    ? "bg-emerald-500"
                    : Number(sellingMasterData.receivedAmount || 0) > 0
                    ? "bg-amber-500"
                    : "bg-rose-500"
                }`}
              />
              {sellingMasterData.isReceived
                ? "Completed"
                : Number(sellingMasterData.receivedAmount || 0) > 0
                ? "Partially Received"
                : "Pending"}
            </span>

            <Button
              variant={sellingMasterData.isReceived ? "secondary" : "primary"}
              size="sm"
              className={`flex items-center gap-1.5 font-bold shadow-sm ${
                sellingMasterData.isReceived
                  ? "border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-400 dark:bg-amber-950/30"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"
              }`}
              onClick={handleToggleReceivedStatus}
              disabled={isTogglingReceived}
              title={
                sellingMasterData.isReceived
                  ? "Unlock payment to add/edit transactions"
                  : "Mark invoice payment as completed and lock"
              }
            >
              <i className="material-icons text-base">
                {sellingMasterData.isReceived ? "lock_open" : "task_alt"}
              </i>
              {sellingMasterData.isReceived ? "Unlock Payment" : "Complete Payment"}
            </Button>
          </div>
        )}
      </div>

      {error && <ErrorAlert message={error} />}

      {sellingMasterData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Card 1: Total Receivable */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                  Total Receivable
                </p>
                <h3 className="text-xl font-black text-slate-800 dark:text-white font-mono">
                  ₹
                  {Number(sellingMasterData.totalAmount || 0).toLocaleString(
                    "en-IN",
                    { minimumFractionDigits: 2 },
                  )}
                </h3>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold mt-1">
                  ₹{Number(sellingMasterData.invoiceAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} (Invoice) - ₹{Number(sellingMasterData.tdsAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} (2% TDS)
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-500 dark:bg-slate-800">
                <i className="material-icons text-xl">account_balance_wallet</i>
              </div>
            </div>

            {/* Card 2: Total Received */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                  Total Received
                </p>
                <h3 className="text-xl font-black text-emerald-600 dark:text-emerald-400 font-mono">
                  ₹
                  {Number(sellingMasterData.receivedAmount || 0).toLocaleString(
                    "en-IN",
                    { minimumFractionDigits: 2 },
                  )}
                </h3>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400">
                <i className="material-icons text-xl">check_circle</i>
              </div>
            </div>

            {/* Card 3: Remaining Balance */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                  {Number(sellingMasterData.remainingReceivableAmount || 0) < 0
                    ? "Excess Received"
                    : "Remaining Balance"}
                </p>
                <h3
                  className={`text-xl font-black font-mono ${
                    Number(sellingMasterData.remainingReceivableAmount || 0) < 0
                      ? "text-indigo-600 dark:text-indigo-400"
                      : Number(sellingMasterData.remainingReceivableAmount || 0) === 0
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-amber-600"
                  }`}
                >
                  ₹
                  {Number(sellingMasterData.remainingReceivableAmount || 0) < 0
                    ? `+${Math.abs(Number(sellingMasterData.remainingReceivableAmount)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                    : Number(
                        sellingMasterData.remainingReceivableAmount || 0,
                      ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </h3>
                {Number(sellingMasterData.remainingReceivableAmount || 0) < 0 && (
                  <p className="text-[10px] text-indigo-500 font-semibold mt-1">
                    Advance / Overpayment
                  </p>
                )}
              </div>
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                  Number(sellingMasterData.remainingReceivableAmount || 0) < 0
                    ? "bg-indigo-50 text-indigo-600 dark:bg-indigo-950/30 dark:text-indigo-400"
                    : Number(sellingMasterData.remainingReceivableAmount || 0) === 0
                    ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400"
                    : "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400"
                }`}
              >
                <i className="material-icons text-xl">
                  {Number(sellingMasterData.remainingReceivableAmount || 0) <= 0
                    ? "verified"
                    : "pending_actions"}
                </i>
              </div>
            </div>
          </div>

          {/* Grid Split Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Side: Payment History */}
            <div className="lg:col-span-8 space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-slate-50/50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <h2 className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-3">
                    <i className="material-icons text-sm">history</i>
                    Transaction History
                    <span className="px-2 py-0.5 rounded-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-[10px]">
                      {payments.length} Records
                    </span>
                  </h2>
                </div>

                <div className="hidden md:block overflow-x-auto">
                  {(() => {
                    const actionsVisible = true;
                    return (
                      <table className="w-full text-left text-xs">
                        <thead>
                          <tr className="bg-slate-50/30 dark:bg-slate-800/30 text-[10px] font-black uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-800 whitespace-nowrap">
                            <th className="px-5 py-3 text-center">Sr. No.</th>
                            <th className="px-5 py-3">Date</th>
                            <th className="px-5 py-3">Payment Mode</th>
                            <th className="px-5 py-3 text-right">
                              Taxable Amount (₹)
                            </th>
                            <th className="px-5 py-3 text-right">
                              GST (18%) (₹)
                            </th>
                            <th className="px-5 py-3 text-right">
                              <div className="flex flex-col items-end leading-tight">
                                <span>TDS Amount (₹)</span>
                                <span className="text-[9px] text-slate-400 dark:text-slate-500 font-normal normal-case tracking-normal block mt-0.5">
                                  (2% of Taxable)
                                </span>
                              </div>
                            </th>
                            <th className="px-5 py-3 text-right">
                              Net Received (₹)
                            </th>
                            <th className="px-5 py-3 min-w-[250px]">Details</th>
                            <th className="px-5 py-3 text-center">Receipt</th>
                            <th className="px-5 py-3">Remarks</th>
                            {actionsVisible && (
                              <th className="px-5 py-3 text-center">Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {payments.length === 0 ? (
                            <tr>
                              <td
                                colSpan={actionsVisible ? "11" : "10"}
                                className="px-5 py-12 text-center text-slate-400 italic"
                              >
                                No payment transactions recorded for this
                                invoice yet.
                              </td>
                            </tr>
                          ) : (
                            payments.map((p, index) => (
                              <tr
                                key={p.id}
                                className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                              >
                                <td className="px-5 py-3 text-center font-medium">
                                  {index + 1}
                                </td>
                                <td className="px-5 py-3 font-medium">
                                  {formatDate(p.date)}
                                </td>
                                <td className="px-5 py-3 whitespace-nowrap">
                                  <span className="px-2.5 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-[11px] font-bold whitespace-nowrap">
                                    {p.paymentMode?.paymentMode ||
                                      `Mode #${p.paymentModeId}`}
                                  </span>
                                </td>
                                <td className="px-5 py-3 text-right font-mono font-bold text-slate-700 dark:text-slate-300">
                                  ₹
                                  {Number(p.amount || 0).toLocaleString(
                                    "en-IN",
                                    { minimumFractionDigits: 2 },
                                  )}
                                </td>
                                <td className="px-5 py-3 text-right font-mono text-slate-500 dark:text-slate-400">
                                  ₹
                                  {Number(p.gstAmount || 0).toLocaleString(
                                    "en-IN",
                                    { minimumFractionDigits: 2 },
                                  )}
                                </td>
                                <td className="px-5 py-3 text-right font-mono text-amber-600 dark:text-amber-400 font-bold">
                                  <div className="flex flex-col items-end">
                                    <span>
                                      ₹
                                      {Number(p.tdsAmount || 0).toLocaleString(
                                        "en-IN",
                                        { minimumFractionDigits: 2 },
                                      )}
                                    </span>
                                    {Number(p.tdsAmount) > 0 && (
                                      <span className="text-[9px] text-slate-400 dark:text-slate-500 font-normal">
                                        2% of ₹{Number(p.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-5 py-3 text-right font-mono font-extrabold text-emerald-600 dark:text-emerald-400">
                                  ₹
                                  {Number(p.netAmount || 0).toLocaleString(
                                    "en-IN",
                                    { minimumFractionDigits: 2 },
                                  )}
                                </td>
                                <td className="px-5 py-3 text-slate-600 dark:text-slate-300 font-medium min-w-[250px]">
                                  {(() => {
                                    const bankText = p.bank ? `${p.bank.bankName}${p.bank.accountNumber ? ` (${p.bank.accountNumber})` : ""}` : null;
                                    const key = p.paymentMode?.systemKey || "";

                                    return (
                                      <div className="flex flex-col gap-0.5 text-xs py-1">
                                        {key === "cash" && (
                                          <div>
                                            <span className="font-bold text-slate-400 dark:text-slate-500">Cash Source:</span>{" "}
                                            <span className="text-slate-700 dark:text-slate-300 font-semibold">
                                              {bankText ? "Bank Deposit" : "Cash in Hand"}
                                            </span>
                                          </div>
                                        )}
                                        {bankText && (
                                          <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{bankText}</span></div>
                                        )}
                                        {key === "bank" && p.transactionRefNo && (
                                          <div><span className="font-bold text-slate-400 dark:text-slate-500">UTR:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.transactionRefNo}</span></div>
                                        )}
                                        {key === "upi" && (
                                          <>
                                            {p.vpa && <div><span className="font-bold text-slate-400 dark:text-slate-500">UPI:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.vpa}</span></div>}
                                            {p.transactionRefNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">ID:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.transactionRefNo}</span></div>}
                                          </>
                                        )}
                                        {key === "cheque" && (
                                          <>
                                            {p.chequeNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">Cheque:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">#{p.chequeNo}</span></div>}
                                            {p.chequeDate && <div><span className="font-bold text-slate-400 dark:text-slate-500">Date:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{formatDate(p.chequeDate)}</span></div>}
                                          </>
                                        )}
                                        {key !== "cash" && !bankText && !p.transactionRefNo && !p.vpa && !p.chequeNo && (
                                          <span className="text-slate-400">N/A</span>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td className="px-5 py-3 text-center">
                                  {p.attachmentPath ? (
                                    <a
                                      href={`${API_CONFIG.baseUrl}/${p.attachmentPath.replace(/\\/g, "/")}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-bold hover:underline"
                                    >
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      View
                                    </a>
                                  ) : (
                                    <span className="text-slate-400">N/A</span>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-slate-600 dark:text-slate-300 max-w-[200px]">
                                  <div className="max-w-[200px] overflow-x-auto whitespace-nowrap custom-scrollbar" title={p.remarks || p.remark || ""}>
                                    {p.remarks || p.remark || "N/A"}
                                  </div>
                                </td>
                                {actionsVisible && (
                                  <td className="px-5 py-3 text-center">
                                    <div className="flex items-center justify-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (sellingMasterData?.isReceived) {
                                            alertService.info("This invoice payment is locked. Please click 'Unlock Payment' above to edit transactions.");
                                            return;
                                          }
                                          handleEditTransaction(p);
                                        }}
                                        className={`p-1.5 rounded-lg transition-all ${
                                          sellingMasterData?.isReceived
                                            ? "text-slate-300 dark:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                                            : "text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/20"
                                        }`}
                                        title={sellingMasterData?.isReceived ? "Payment is locked. Unlock to edit" : "Edit"}
                                      >
                                        <svg
                                          className="h-4 w-4"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                          />
                                        </svg>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (sellingMasterData?.isReceived) {
                                            alertService.info("This invoice payment is locked. Please click 'Unlock Payment' above to delete transactions.");
                                            return;
                                          }
                                          handleDeleteTransaction(p);
                                        }}
                                        className={`p-1.5 rounded-lg transition-all ${
                                          sellingMasterData?.isReceived
                                            ? "text-slate-300 dark:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                                            : "text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20"
                                        }`}
                                        title={sellingMasterData?.isReceived ? "Payment is locked. Unlock to delete" : "Delete"}
                                      >
                                        <svg
                                          className="h-4 w-4"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>

                {/* Mobile View: Cards */}
                <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {payments.length === 0 ? (
                    <div className="px-5 py-8 text-center text-slate-400 italic">
                      No payment transactions recorded for this invoice yet.
                    </div>
                  ) : (
                    payments.map((p, index) => {
                      const remarksText = p.remarks || p.remark || "N/A";
                      const detailsContent = (() => {
                        const bankText = p.bank ? `${p.bank.bankName}${p.bank.accountNumber ? ` (${p.bank.accountNumber})` : ""}` : null;
                        const key = p.paymentMode?.systemKey || "";

                        return (
                          <div className="flex flex-col gap-0.5">
                            {key === "cash" && (
                              <div>
                                <span className="font-bold text-slate-400 dark:text-slate-500">Cash Source:</span>{" "}
                                <span className="font-medium text-slate-700 dark:text-slate-300">
                                  {bankText ? "Bank Deposit" : "Cash in Hand"}
                                </span>
                              </div>
                            )}
                            {bankText && (
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{bankText}</span></div>
                            )}
                            {key === "bank" && p.transactionRefNo && (
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">UTR:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.transactionRefNo}</span></div>
                            )}
                            {key === "upi" && (
                              <>
                                {p.vpa && <div><span className="font-bold text-slate-400 dark:text-slate-500">UPI:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.vpa}</span></div>}
                                {p.transactionRefNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">ID:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.transactionRefNo}</span></div>}
                              </>
                            )}
                            {key === "cheque" && (
                              <>
                                {p.chequeNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">Cheque:</span> <span className="font-medium text-slate-700 dark:text-slate-300">#{p.chequeNo}</span></div>}
                                {p.chequeDate && <div><span className="font-bold text-slate-400 dark:text-slate-500">Date:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{formatDate(p.chequeDate)}</span></div>}
                              </>
                            )}
                          </div>
                        );
                      })();

                      return (
                        <div key={p.id} className="p-4 space-y-3 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[10px] font-black text-slate-500">
                                {index + 1}
                              </span>
                              <span className="font-bold text-slate-500">{formatDate(p.date)}</span>
                            </div>
                            <span className="px-2 py-0.5 rounded bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-[10px] font-bold">
                              {p.paymentMode?.paymentMode || `Mode #${p.paymentModeId}`}
                            </span>
                          </div>

                          <div className="space-y-1 bg-slate-50/50 dark:bg-slate-800/10 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500 font-bold">Taxable Amount:</span>
                              <span className="font-semibold text-slate-700 dark:text-slate-300 font-mono">
                                ₹{Number(p.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </span>
                            </div>

                            {Number(p.gstAmount) > 0 && (
                              <div className="flex items-center justify-between">
                                <span className="text-slate-500 font-bold">GST (18%):</span>
                                <span className="font-semibold text-slate-600 dark:text-slate-400 font-mono">
                                  ₹{Number(p.gstAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </span>
                              </div>
                            )}

                            {Number(p.tdsAmount) > 0 && (
                              <div className="flex items-center justify-between">
                                <span className="text-slate-500 font-bold">TDS (2%):</span>
                                <span className="font-bold text-amber-600 dark:text-amber-400 font-mono flex flex-col items-end">
                                  <span>₹{Number(p.tdsAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                                  <span className="text-[9px] text-slate-400 dark:text-slate-500 font-normal">
                                    (2% of ₹{Number(p.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })})
                                  </span>
                                </span>
                              </div>
                            )}

                            <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-1.5 mt-1">
                              <span className="text-slate-700 dark:text-slate-300 font-bold">Net Received:</span>
                              <span className="font-black text-emerald-600 dark:text-emerald-400 font-mono">
                                ₹{Number(p.netAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>

                          {detailsContent && (
                            <div className="text-[11px] text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800">
                              {detailsContent}
                            </div>
                          )}

                          {remarksText !== "-" && (
                            <div className="text-[11px] text-slate-500 italic">
                              <span className="font-bold text-slate-400">Remarks: </span>{remarksText}
                            </div>
                          )}

                          <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-2 mt-2">
                            <div>
                              {p.attachmentPath ? (
                                <a
                                  href={`${API_CONFIG.baseUrl}/${p.attachmentPath.replace(/\\/g, "/")}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] text-primary-600 hover:text-primary-700 font-bold"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  View Receipt
                                </a>
                              ) : (
                                <span className="text-[10px] text-slate-400 italic">No receipt</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  if (sellingMasterData?.isReceived) {
                                    alertService.info("This invoice payment is locked. Please click 'Unlock Payment' above to edit transactions.");
                                    return;
                                  }
                                  handleEditTransaction(p);
                                }}
                                className={`p-1.5 rounded-lg transition-all text-xs flex items-center gap-1 border ${
                                  sellingMasterData?.isReceived
                                    ? "text-slate-400 border-slate-200 dark:border-slate-800"
                                    : "text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/20 border-blue-200/50 dark:border-blue-950/50"
                                }`}
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (sellingMasterData?.isReceived) {
                                    alertService.info("This invoice payment is locked. Please click 'Unlock Payment' above to delete transactions.");
                                    return;
                                  }
                                  handleDeleteTransaction(p);
                                }}
                                className={`p-1.5 rounded-lg transition-all text-xs flex items-center gap-1 border ${
                                  sellingMasterData?.isReceived
                                    ? "text-slate-400 border-slate-200 dark:border-slate-800"
                                    : "text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20 border-rose-200/50 dark:border-rose-950/50"
                                }`}
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Right Side: Form or Locked Card */}
            <div className="lg:col-span-4">
              {Boolean(sellingMasterData.isReceived) && !isEditingTransaction ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 text-center dark:border-emerald-900/40 dark:bg-emerald-950/30 sticky top-6 animate-in zoom-in duration-300 shadow-sm">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400">
                      <i className="material-icons text-3xl">task_alt</i>
                    </div>
                    <h3 className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-tight">
                      Payment Completed & Locked
                    </h3>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest leading-relaxed">
                      This invoice has been marked as fully completed and locked.
                    </p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-relaxed">
                      To add additional payments or modify existing transactions, click the button below to unlock.
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-2 flex items-center gap-1.5 font-bold border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-400 dark:bg-amber-950/30"
                      onClick={handleToggleReceivedStatus}
                      disabled={isTogglingReceived}
                    >
                      <i className="material-icons text-sm">lock_open</i>
                      Unlock Payment
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sticky top-6">
                  <h2 className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-wider mb-4 border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">
                      {isEditingTransaction ? "✎" : "+"}
                    </span>
                    {isEditingTransaction ? "Edit Payment" : "Add Payment"}
                  </h2>

                  <form onSubmit={handleSubmitTransaction} className="space-y-4">
                    {/* --- Payment Date --- */}
                    <div>
                      <DatePicker
                        label="DATE"
                        required={true}
                        value={transactionForm.date}
                        onChange={(val) => handleFormChange("date", val)}
                        error={formErrors.date}
                        placeholder="YYYY-MM-DD"
                        minDate={sellingMasterData?.date}
                        labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                      />
                    </div>

                    {/* --- Payment Mode --- */}
                    <div>
                      <SearchableDropdown
                        label={
                          <>
                            MODE <span className="text-rose-500">*</span>
                          </>
                        }
                        value={transactionForm.paymentModeId}
                        options={paymentModes}
                        placeholder="Select Mode"
                        onChange={(e) =>
                          handleFormChange(
                            "paymentModeId",
                            e.target.value,
                          )
                        }
                        error={formErrors.paymentModeId}
                        labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                      />
                    </div>

                    {/* --- Amount (₹) --- */}
                    <div>
                      <Input
                        label={
                          <>
                            AMOUNT (₹) <span className="text-rose-500">*</span>
                          </>
                        }
                        type="number"
                        min="0"
                        value={transactionForm.amount}
                        onChange={(e) =>
                          handleAmountChange(e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "E") {
                            e.preventDefault();
                          }
                        }}
                        placeholder="0.00"
                        error={formErrors.amount}
                        className="font-bold font-mono"
                        labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                      />
                    </div>

                    {/* --- Dynamic Payment Details Box (Each Field on Its Own Row) --- */}
                    {(() => {
                      const selectedMode = paymentModes.find(m => String(m.value) === String(transactionForm.paymentModeId));
                      const sysKey = selectedMode ? (selectedMode.systemKey || "") : "";
                      if (!transactionForm.paymentModeId) return null;

                      if (sysKey === "cash") {
                        return (
                          <div className="rounded-xl border border-amber-200/80 dark:border-amber-900/40 bg-amber-50/20 dark:bg-slate-800/40 p-4 space-y-4 shadow-sm animate-in fade-in duration-200">
                            <div className="text-[10px] font-black text-amber-700 dark:text-amber-400 uppercase tracking-widest flex items-center gap-1.5 border-b border-amber-200/60 dark:border-amber-900/40 pb-2.5">
                              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-600 text-[10px] font-bold text-white">
                                i
                              </span>
                              {selectedMode?.label || "Payment"} Details
                            </div>

                            <div className="space-y-4">
                              {/* Cash Source */}
                              <div className="space-y-1">
                                <SearchableDropdown
                                  label={
                                    <>
                                      Cash Source <span className="text-rose-500">*</span>
                                    </>
                                  }
                                  value={transactionForm.cashSource}
                                  options={[
                                    { label: "Cash in Hand", value: "Cash in Hand" },
                                    { label: "Bank Deposit", value: "Bank Deposit" }
                                  ]}
                                  placeholder="Select Cash Source"
                                  onChange={(e) => handleFormChange("cashSource", e.target.value)}
                                  error={formErrors.cashSource}
                                  labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                />
                              </div>

                              {/* Bank field if Bank Deposit */}
                              {transactionForm.cashSource === "Bank Deposit" && (
                                <div className="space-y-1 animate-in fade-in duration-200">
                                  <SearchableDropdown
                                    label={
                                      <>
                                        BANK <span className="text-rose-500">*</span>
                                      </>
                                    }
                                    value={transactionForm.bankId}
                                    options={activeBanks}
                                    placeholder="Select Bank"
                                    onChange={(e) => handleFormChange("bankId", e.target.value)}
                                    error={formErrors.bankId}
                                    labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                  />
                                  {selectedBank && (
                                    <div className="mt-2 p-3 rounded-xl bg-blue-50/80 dark:bg-blue-950/40 border border-blue-200/80 dark:border-blue-800/50 shadow-sm transition-all duration-200 space-y-1.5 text-xs">
                                      <div className="flex items-center justify-between font-bold text-slate-800 dark:text-slate-100">
                                        <span className="flex items-center gap-1.5 text-blue-700 dark:text-blue-400 font-extrabold text-xs">
                                          <i className="material-icons text-sm">account_balance</i>
                                          {selectedBank.bankName}
                                        </span>
                                        <span className="px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-300 font-mono text-[10px] font-bold">
                                          A/C: {selectedBank.accountNumber}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-2 text-[11px] pt-1.5 text-slate-600 dark:text-slate-400 border-t border-blue-100 dark:border-blue-900/40">
                                        <div>
                                          <span className="font-semibold text-slate-500 dark:text-slate-400">IFSC Code:</span>{" "}
                                          <span className="font-mono font-bold text-slate-700 dark:text-slate-200">{selectedBank.ifscCode || "N/A"}</span>
                                        </div>
                                        {selectedBank.utrReferenceNumber && (
                                          <div>
                                            <span className="font-semibold text-slate-500 dark:text-slate-400">Default UTR:</span>{" "}
                                            <span className="font-mono font-bold text-slate-700 dark:text-slate-200">{selectedBank.utrReferenceNumber}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="rounded-xl border border-amber-200/80 dark:border-amber-900/40 bg-amber-50/20 dark:bg-slate-800/40 p-4 space-y-4 shadow-sm animate-in fade-in duration-200">
                          <div className="text-[10px] font-black text-amber-700 dark:text-amber-400 uppercase tracking-widest flex items-center gap-1.5 border-b border-amber-200/60 dark:border-amber-900/40 pb-2.5">
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-600 text-[10px] font-bold text-white">
                              i
                            </span>
                            {selectedMode?.label || "Payment"} Details
                          </div>

                          <div className="space-y-4">
                            {/* Row 1: Bank */}
                            <div className="space-y-1">
                              <SearchableDropdown
                                label={
                                  <>
                                    BANK <span className="text-rose-500">*</span>
                                  </>
                                }
                                value={transactionForm.bankId}
                                options={activeBanks}
                                placeholder="Select Bank"
                                onChange={(e) => handleFormChange("bankId", e.target.value)}
                                error={formErrors.bankId}
                                labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                              />
                              {selectedBank && (
                                <div className="mt-2 p-3 rounded-xl bg-blue-50/80 dark:bg-blue-950/40 border border-blue-200/80 dark:border-blue-800/50 shadow-sm transition-all duration-200 space-y-1.5 text-xs">
                                  <div className="flex items-center justify-between font-bold text-slate-800 dark:text-slate-100">
                                    <span className="flex items-center gap-1.5 text-blue-700 dark:text-blue-400 font-extrabold text-xs">
                                      <i className="material-icons text-sm">account_balance</i>
                                      {selectedBank.bankName}
                                    </span>
                                    <span className="px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-300 font-mono text-[10px] font-bold">
                                      A/C: {selectedBank.accountNumber}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-[11px] pt-1.5 text-slate-600 dark:text-slate-400 border-t border-blue-100 dark:border-blue-900/40">
                                    <div>
                                      <span className="font-semibold text-slate-500 dark:text-slate-400">IFSC Code:</span>{" "}
                                      <span className="font-mono font-bold text-slate-700 dark:text-slate-200">{selectedBank.ifscCode || "N/A"}</span>
                                    </div>
                                    {selectedBank.utrReferenceNumber && (
                                      <div>
                                        <span className="font-semibold text-slate-500 dark:text-slate-400">Default UTR:</span>{" "}
                                        <span className="font-mono font-bold text-slate-700 dark:text-slate-200">{selectedBank.utrReferenceNumber}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Row 2 (if Bank mode): UTR / Ref No */}
                            {sysKey === "bank" && (
                              <div>
                                <Input
                                  label="UTR / REFERENCE NUMBER"
                                  type="text"
                                  value={transactionForm.transactionRefNo}
                                  onChange={(e) => handleFormChange("transactionRefNo", e.target.value)}
                                  placeholder="UTR reference number"
                                  error={formErrors.transactionRefNo}
                                  labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                />
                              </div>
                            )}

                            {/* Row 2 (if UPI mode): UPI ID */}
                            {sysKey === "upi" && (
                              <div>
                                <Input
                                  label="UPI ID (VPA)"
                                  type="text"
                                  value={transactionForm.vpa}
                                  onChange={(e) => handleFormChange("vpa", e.target.value)}
                                  placeholder="e.g., username@upi"
                                  error={formErrors.vpa}
                                  labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                />
                              </div>
                            )}

                            {/* Row 3 (if UPI mode): Transaction ID */}
                            {sysKey === "upi" && (
                              <div>
                                <Input
                                  label="TRANSACTION ID"
                                  type="text"
                                  value={transactionForm.transactionRefNo}
                                  onChange={(e) => handleFormChange("transactionRefNo", e.target.value)}
                                  placeholder="UPI reference / transaction ID"
                                  error={formErrors.transactionRefNo}
                                  labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                />
                              </div>
                            )}

                            {/* Row 2 (if Cheque mode): Cheque Number */}
                            {sysKey === "cheque" && (
                              <div>
                                <Input
                                  label="CHEQUE NUMBER"
                                  type="text"
                                  value={transactionForm.chequeNo}
                                  onChange={(e) => handleFormChange("chequeNo", e.target.value)}
                                  placeholder="Cheque number"
                                  error={formErrors.chequeNo}
                                  maxLength={10}
                                  labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                />
                              </div>
                            )}

                            {/* Row 3 (if Cheque mode): Cheque Date */}
                            {sysKey === "cheque" && (
                              <div>
                                <DatePicker
                                  label="CHEQUE DATE"
                                  value={transactionForm.chequeDate}
                                  onChange={(val) => handleFormChange("chequeDate", val)}
                                  error={formErrors.chequeDate}
                                  labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                />
                              </div>
                            )}

                            {/* Row (Last in Details): Attachment */}
                            <div>
                              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                                ATTACHMENT
                              </label>
                              {transactionForm.attachment || (transactionForm.attachmentPath && transactionForm.attachmentPath !== "") ? (
                                <div className="flex items-center justify-between py-1.5 px-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm animate-in fade-in duration-200">
                                  <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200 truncate pr-2" title={
                                    transactionForm.attachment
                                      ? transactionForm.attachment.name
                                      : transactionForm.attachmentPath.split(/[\\/]/).pop()
                                  }>
                                    {transactionForm.attachment
                                      ? transactionForm.attachment.name
                                      : transactionForm.attachmentPath.split(/[\\/]/).pop()}
                                  </span>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (transactionForm.attachment) {
                                          const fileUrl = URL.createObjectURL(transactionForm.attachment);
                                          window.open(fileUrl, "_blank");
                                        } else if (transactionForm.attachmentPath) {
                                          window.open(`${API_CONFIG.baseUrl}/${transactionForm.attachmentPath.replace(/\\/g, "/")}`, "_blank");
                                        }
                                      }}
                                      className="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors shadow-sm active:scale-95"
                                      title="View Attachment"
                                    >
                                      <i className="material-icons text-amber-700 dark:text-amber-500" style={{ fontSize: "14px" }}>visibility</i>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setTransactionForm(prev => ({
                                          ...prev,
                                          attachment: null,
                                          attachmentPath: ""
                                        }));
                                        const fileInput = document.getElementById("payment-attachment-input");
                                        if (fileInput) fileInput.value = "";
                                      }}
                                      className="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors shadow-sm active:scale-95"
                                      title="Remove Attachment"
                                    >
                                      <i className="material-icons text-rose-500 dark:text-rose-400" style={{ fontSize: "14px" }}>close</i>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <input
                                  id="payment-attachment-input"
                                  type="file"
                                  onChange={(e) => handleFormChange("attachment", e.target.files[0])}
                                  accept="application/pdf,image/jpeg,image/png"
                                  className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-amber-100/60 file:text-amber-800 hover:file:bg-amber-100 cursor-pointer"
                                />
                              )}
                              <p className="text-[10px] text-rose-500 font-semibold mt-1">
                                * Only PDF, JPG, PNG. Maximum Size: 5 MB
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* --- Remarks --- */}
                    <div>
                      <TextArea
                        label="REMARKS"
                        value={transactionForm.remarks}
                        onChange={(e) =>
                          handleFormChange("remarks", e.target.value)
                        }
                        placeholder="Add remarks (optional)"
                        error={formErrors.remarks}
                        className="font-medium"
                        labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                        rows={2}
                      />
                    </div>

                    {/* --- TDS Included Checkbox --- */}
                    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 p-3">
                      <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={transactionForm.isTds}
                          onChange={(e) => handleTdsToggle(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500/20 cursor-pointer"
                        />
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                            TDS Included / Deducted (2%)
                          </span>
                          <span className="text-[10px] text-slate-400 dark:text-slate-500">
                            {transactionForm.isTds
                              ? `2% TDS (₹${Number(transactionForm.tdsAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}) will be deducted from net received`
                              : "No TDS will be deducted for this payment"}
                          </span>
                        </div>
                      </label>
                    </div>

                    {/* --- Active Status (Only in Edit Mode) --- */}
                    {isEditingTransaction && (
                      <div>
                        <label className="flex cursor-pointer items-center gap-2">
                          <input
                            type="checkbox"
                            checked={transactionForm.isActive}
                            onChange={(e) =>
                              handleFormChange(
                                "isActive",
                                e.target.checked,
                              )
                            }
                            className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-600/20"
                          />
                          <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300 uppercase">
                            Active
                          </span>
                        </label>
                      </div>
                    )}

                    {/* --- Action Buttons --- */}
                    <div className="pt-2 flex gap-2">
                      <Button
                        type="submit"
                        className="flex-1"
                        isLoading={isSubmittingTransaction}
                      >
                        {isEditingTransaction
                          ? "Update Payment"
                          : "Save Payment"}
                      </Button>
                      {isEditingTransaction && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={resetTransactionForm}
                          className="text-slate-500"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SalesPaymentDetailPage;
